import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { DismissableLayerProps } from '@radix-ui/react-dismissable-layer';
import { X } from 'lucide-react';

import { cn } from '~/common/utils/styleUtil';
import { useTranslation } from 'react-i18next';
import { useTouchDevice } from '~/common/hooks/useTouchDevice.ts';

type InteractOutsideEvent = Parameters<NonNullable<DismissableLayerProps['onInteractOutside']>>[0];

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    showFullscreenOnMobile?: boolean;
    overlayClassName?: string;
    showClose?: boolean;
    closeIcon?: React.ReactNode;
    verticalPosition?: 'center' | 'top';
  }
>(
  (
    {
      className,
      children,
      showFullscreenOnMobile = false,
      overlayClassName,
      showClose = true,
      closeIcon,
      verticalPosition,
      onInteractOutside,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();

    const isTouchDevice = useTouchDevice();

    const handleInteractOutside = (event: InteractOutsideEvent) => {
      const hasOpenRadixPortal = document.querySelector(
        '[data-radix-popper-content-wrapper], [data-radix-select-content]'
      );
      if (hasOpenRadixPortal) {
        event.preventDefault();
        return;
      }
      onInteractOutside?.(event);
    };
    if (!verticalPosition) {
      verticalPosition = isTouchDevice ? 'top' : 'center';
    }

    const getPositionClasses = () => {
      if (verticalPosition === 'top') {
        return 'inset-0 left-[50%] top-0 mt-6 flex flex-col max-w-lg max-h-[90vh] translate-x-[-50%] translate-y-0 sm:rounded-lg h-fit';
      }
      return 'inset-0 left-[50%] top-[50%] flex flex-col max-w-lg max-h-[90vh] translate-x-[-50%] translate-y-[-50%] sm:rounded-lg h-fit';
    };

    const getFullscreenPositionClasses = () => {
      if (verticalPosition === 'top') {
        return 'inset-0 h-[100dvh] max-h-dvh grid-rows-[auto_1fr_auto] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top sm:inset-0 sm:left-[50%] sm:top-0 sm:mt-6 sm:flex sm:flex-col sm:max-w-lg sm:max-h-[90vh] sm:translate-x-[-50%] sm:translate-y-0 sm:rounded-lg sm:h-fit';
      }
      return 'inset-0 h-[100dvh] max-h-dvh grid-rows-[auto_1fr_auto] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top sm:inset-0 sm:left-[50%] sm:top-[50%] sm:flex sm:flex-col sm:max-w-lg sm:max-h-[90vh] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-lg sm:h-fit';
    };

    return (
      <DialogPortal>
        <DialogOverlay className={overlayClassName} />
        <DialogPrimitive.Content
          ref={ref}
          onInteractOutside={handleInteractOutside}
          className={cn(
            'fixed z-50 grid w-full gap-4 border bg-background p-4 shadow-lg duration-200',
            showFullscreenOnMobile ? getFullscreenPositionClasses() : getPositionClasses(),
            className
          )}
          {...props}
        >
          {children}
          {showClose && (
            <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
              {closeIcon ?? <X className="h-5 w-5" />}
              <span className="sr-only">{t('Schliessen')}</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }
);

DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 sm:text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...props} />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-xl font-semibold leading-none tracking-tight mb-4 mr-8', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn('text-sm', className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription
};
